import React from 'react';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const { LuCar, LuShip, LuFileText, LuCalculator, LuInfo } = LuIcons;

// What's included badge
const IncludesBadge = ({ priceIncludes, hasCustoms, hasShipping }) => {
  if (!priceIncludes && !hasCustoms && !hasShipping) return null;

  // Determine badge type
  const isUnknown = priceIncludes === 'السعر غير محدد';
  const noShipping = !hasShipping;
  const noCustoms = !hasCustoms;

  if (isUnknown) {
    return (
      <div className="flex items-center gap-1.5 bg-slate-100 text-slate-500 text-xs font-bold px-3 py-1.5 rounded-lg">
        <SafeIcon icon={LuInfo} className="w-3.5 h-3.5 flex-shrink-0" />
        <span>السعر غير محدد - تواصل للاستفسار</span>
      </div>
    );
  }

  // Build what's NOT included tags
  const missing = [];
  if (noShipping) missing.push('الشحن');
  if (noCustoms) missing.push('الجمركة');

  if (missing.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {missing.map((item) => (
        <span
          key={item}
          className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold px-2 py-0.5 rounded-full"
        >
          <span className="text-amber-400">⚠</span> بدون {item}
        </span>
      ))}
    </div>
  );
};

const PricingBreakdown = ({ car, isSold }) => {
  const hasBreakdown =
    Number(car.customs_price) > 0 || Number(car.shipping_price) > 0;

  const displayTotal =
    Number(car.total_price) > 0
      ? Number(car.total_price)
      : (Number(car.price) || 0) +
        (Number(car.customs_price) || 0) +
        (Number(car.shipping_price) || 0);

  const isUnknown = car.price_includes === 'السعر غير محدد' || (Number(car.price) === 0 && displayTotal === 0);

  if (!car.price && !hasBreakdown && !isUnknown) return null;

  const base = isSold
    ? {
        border: 'border-slate-200',
        bg: 'bg-slate-50',
        iconBg: 'bg-slate-200',
        iconColor: 'text-slate-500',
        text: 'text-slate-600',
        label: 'text-slate-500',
        divider: 'divide-slate-100',
        rowBg: 'bg-white',
      }
    : {
        border: 'border-emerald-100',
        bg: 'bg-emerald-50/40',
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        text: 'text-emerald-700',
        label: 'text-emerald-700',
        divider: 'divide-slate-100',
        rowBg: 'bg-white',
      };

  const rows = [
    {
      label: 'سعر السيارة',
      value: car.price,
      icon: LuCar,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      show: Number(car.price) > 0,
    },
    {
      label: 'سعر الجمركة',
      value: car.customs_price,
      icon: LuFileText,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      show: Number(car.customs_price) > 0,
    },
    {
      label: 'سعر الشحن',
      value: car.shipping_price,
      icon: LuShip,
      color: 'text-violet-600',
      bg: 'bg-violet-50',
      show: Number(car.shipping_price) > 0,
    },
  ].filter((r) => r.show);

  // Unknown price
  if (isUnknown) {
    return (
      <div className={`rounded-xl border overflow-hidden ${base.border} ${base.bg}`}>
        <div className="flex items-center gap-2 px-3 py-3">
          <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${base.iconBg}`}>
            <SafeIcon icon={LuInfo} className={`w-3.5 h-3.5 ${base.iconColor}`} />
          </div>
          <span className={`text-xs font-black ${base.label}`}>
            السعر غير محدد
          </span>
          <span className="text-xs text-slate-400 ms-auto">تواصل للاستفسار</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border overflow-hidden ${base.border} ${base.bg}`}>
      {/* Breakdown rows */}
      {hasBreakdown && rows.length > 0 && (
        <div className={`${base.rowBg} divide-y ${base.divider}`}>
          {rows.map((row, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-1.5">
              <div className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-md flex items-center justify-center ${row.bg}`}>
                  <SafeIcon icon={row.icon} className={`w-3 h-3 ${row.color}`} />
                </div>
                <span className="text-xs text-slate-500 font-medium">{row.label}</span>
              </div>
              <span className="text-xs font-bold text-slate-600">
                {Number(row.value).toLocaleString()}{' '}
                <span className="font-normal">دج</span>
              </span>
            </div>
          ))}

          <div className={`flex items-center justify-between px-3 py-2 ${base.bg}`}>
            <div className="flex items-center gap-2">
              <div className={`w-5 h-5 rounded-md flex items-center justify-center ${base.iconBg}`}>
                <SafeIcon icon={LuCalculator} className={`w-3 h-3 ${base.iconColor}`} />
              </div>
              <span className={`text-xs font-black ${base.label}`}>السعر الإجمالي</span>
            </div>
            <span className={`font-black text-sm ${base.text}`}>
              {Number(displayTotal).toLocaleString()}{' '}
              <span className="text-xs font-medium">دج</span>
            </span>
          </div>

          {/* What's not included */}
          <div className="px-3 pb-2 pt-1">
            <IncludesBadge
              priceIncludes={car.price_includes}
              hasCustoms={Number(car.customs_price) > 0}
              hasShipping={Number(car.shipping_price) > 0}
            />
          </div>
        </div>
      )}

      {/* Simple price - no breakdown */}
      {!hasBreakdown && (
        <div>
          <div className="flex items-center justify-between px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${base.iconBg}`}>
                <SafeIcon icon={LuCalculator} className={`w-3.5 h-3.5 ${base.iconColor}`} />
              </div>
              <span className={`text-xs font-black ${base.label}`}>السعر</span>
            </div>
            <span className={`font-black text-base ${base.text}`}>
              {Number(displayTotal).toLocaleString()}{' '}
              <span className="text-xs font-medium">دج</span>
            </span>
          </div>

          {/* What's not included */}
          <div className="px-3 pb-2">
            <IncludesBadge
              priceIncludes={car.price_includes}
              hasCustoms={Number(car.customs_price) > 0}
              hasShipping={Number(car.shipping_price) > 0}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingBreakdown;